import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { checkedCircleIcon } from "../../../common/checkedCircleIcon";
import { cancelIconRed } from "../../../common/cancelIconRed";
import { useAppStore } from "../../../../appStore";
import {
  WireGuardTunnel,
  WireGuardTunnelDraft,
  Dispatcher,
} from "./SiteAboutPageUtils/siteAboutInterfaces";
import { WireGuardTunnelSelect } from "./WireGuardTunnelSelect";
import {
  formatWireguardLastHandshake,
  getSelectedWireguardTunnel,
  resolveWireguardServerEndpoint,
  resolveWireguardServerPublicKey,
} from "./wireguardTabUtils";

export type WireGuardEditSession = {
  drafts: Record<number, WireGuardTunnelDraft>;
  snapshot: WireGuardTunnel[];
};

const headCellSx = {
  fontFamily: "Mulish",
  fontWeight: "bold" as const,
};

const MULTI_SITE_WARNING =
  "Please note that submitting changes to a Wireguard Tunnel applies these changes to all sites which use this tunnel";

const computeAnyDirty = (
  drafts: Record<number, WireGuardTunnelDraft> | null,
  snapshot: WireGuardTunnel[] | null
): boolean => {
  if (!drafts || !snapshot?.length) {
    return false;
  }
  return snapshot.some((s) => {
    const d = drafts[s.id];
    if (!d) {
      return false;
    }
    return (
      d.name !== s.name || d.client_public_key !== s.client_public_key
    );
  });
};

type PendingEdit =
  | { kind: "name"; tunnelId: number; value: string }
  | { kind: "key"; tunnelId: number; value: string };

export const WireGuardTabEditMode = ({
  wireguardTunnels,
  selectedTunnelId,
  onSelectTunnelId,
  onSessionUpdate,
  setWireguardChangesMade,
}: {
  wireguardTunnels: WireGuardTunnel[];
  selectedTunnelId: number | null;
  onSelectTunnelId: (id: number) => void;
  onSessionUpdate: (session: WireGuardEditSession) => void;
  setWireguardChangesMade: Dispatcher<boolean>;
}) => {
  const wireGuardOptions = useAppStore((s) => s.wireGuardOptions);
  const snapshotRef = React.useRef<WireGuardTunnel[] | null>(null);
  if (snapshotRef.current === null) {
    snapshotRef.current = JSON.parse(
      JSON.stringify(wireguardTunnels)
    ) as WireGuardTunnel[];
  }
  const [draftsById, setDraftsById] = React.useState<
    Record<number, WireGuardTunnelDraft>
  >(() =>
    Object.fromEntries(
      wireguardTunnels.map((t) => [
        t.id,
        { name: t.name, client_public_key: t.client_public_key },
      ])
    )
  );
  const multiSiteWarningAcknowledgedRef = React.useRef(false);
  const [multiSiteWarningOpen, setMultiSiteWarningOpen] =
    React.useState(false);
  const [pendingEdit, setPendingEdit] = React.useState<PendingEdit | null>(
    null
  );

  const tunnel = getSelectedWireguardTunnel(
    wireguardTunnels,
    selectedTunnelId
  );

  const applyDraftUpdate = (
    tunnelId: number,
    next: Partial<WireGuardTunnelDraft>
  ) => {
    setDraftsById((prev) => {
      const cur = prev[tunnelId];
      if (!cur) {
        return prev;
      }
      const merged = { ...cur, ...next };
      return { ...prev, [tunnelId]: merged };
    });
  };

  React.useEffect(() => {
    if (snapshotRef.current) {
      onSessionUpdate({
        drafts: draftsById,
        snapshot: snapshotRef.current,
      });
    }
  }, [draftsById, onSessionUpdate]);

  React.useEffect(() => {
    setWireguardChangesMade(
      computeAnyDirty(draftsById, snapshotRef.current)
    );
  }, [draftsById, setWireguardChangesMade]);

  const flushPendingEdit = () => {
    if (!pendingEdit) {
      return;
    }
    if (pendingEdit.kind === "name") {
      applyDraftUpdate(pendingEdit.tunnelId, { name: pendingEdit.value });
    } else {
      applyDraftUpdate(pendingEdit.tunnelId, {
        client_public_key: pendingEdit.value,
      });
    }
    setPendingEdit(null);
  };

  const requestNameChange = (tunnelId: number, value: string) => {
    if (!multiSiteWarningAcknowledgedRef.current) {
      setPendingEdit({ kind: "name", tunnelId, value });
      setMultiSiteWarningOpen(true);
      return;
    }
    applyDraftUpdate(tunnelId, { name: value });
  };

  const requestKeyChange = (tunnelId: number, value: string) => {
    if (!multiSiteWarningAcknowledgedRef.current) {
      setPendingEdit({ kind: "key", tunnelId, value });
      setMultiSiteWarningOpen(true);
      return;
    }
    applyDraftUpdate(tunnelId, { client_public_key: value });
  };

  const handleWarningConfirm = () => {
    multiSiteWarningAcknowledgedRef.current = true;
    setMultiSiteWarningOpen(false);
    flushPendingEdit();
  };

  const handleWarningClose = () => {
    setMultiSiteWarningOpen(false);
    setPendingEdit(null);
  };

  const draft = tunnel ? draftsById[tunnel.id] : null;

  if (!tunnel || !draft) {
    return (
      <p className="font-mulish text-[#283E6D] text-[20px] text-center">
        No WireGuard tunnel data
      </p>
    );
  }

  const snapTunnel =
    snapshotRef.current?.find((t) => t.id === tunnel.id) ?? tunnel;
  const endpoint = resolveWireguardServerEndpoint(
    snapTunnel.server,
    wireGuardOptions
  );
  const serverPk = resolveWireguardServerPublicKey(
    snapTunnel.server,
    wireGuardOptions
  );
  const subnetsText =
    snapTunnel.subnets?.length > 0 ? snapTunnel.subnets.join(", ") : "—";

  return (
    <TableContainer component={Paper} sx={{ width: "100%", maxWidth: "100%" }}>
      <Dialog open={multiSiteWarningOpen} onClose={handleWarningClose}>
        <DialogTitle sx={{ fontFamily: "Mulish" }}>WireGuard tunnel</DialogTitle>
        <DialogContent sx={{ fontFamily: "Mulish" }}>
          {MULTI_SITE_WARNING}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarningClose} sx={{ fontFamily: "Mulish" }}>
            Cancel
          </Button>
          <Button onClick={handleWarningConfirm} sx={{ fontFamily: "Mulish" }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <WireGuardTunnelSelect
        tunnels={wireguardTunnels}
        selectedTunnelId={selectedTunnelId}
        onSelectTunnelId={onSelectTunnelId}
      />
      <Table sx={{ fontFamily: "Mulish" }}>
        <TableBody>
          <TableRow>
            <TableCell sx={headCellSx}>Name</TableCell>
            <TableCell sx={headCellSx}>WireGuard server endpoint</TableCell>
            <TableCell sx={headCellSx}>Server Public Key</TableCell>
            <TableCell sx={headCellSx}>Client Public Key</TableCell>
            <TableCell sx={headCellSx}>Client tunnel IP</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
              <TextField
                value={draft.name}
                variant="standard"
                fullWidth
                sx={{ minWidth: 120, fontFamily: "Mulish" }}
                onChange={(e) => requestNameChange(tunnel.id, e.target.value)}
              />
            </TableCell>
            <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
              {endpoint}
            </TableCell>
            <TableCell
              align="left"
              sx={{ fontFamily: "Mulish", wordBreak: "break-all" }}
            >
              {serverPk}
            </TableCell>
            <TableCell
              align="left"
              sx={{ fontFamily: "Mulish", wordBreak: "break-all" }}
            >
              <TextField
                value={draft.client_public_key}
                variant="standard"
                fullWidth
                sx={{ minWidth: 160, fontFamily: "Mulish" }}
                onChange={(e) =>
                  requestKeyChange(tunnel.id, e.target.value)
                }
              />
            </TableCell>
            <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
              {snapTunnel.client_tunnel_ip || "—"}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={2} sx={headCellSx}>
              Subnets
            </TableCell>
            <TableCell sx={headCellSx}>Subnets are virtually assigned</TableCell>
            <TableCell sx={headCellSx}>Tunnel status</TableCell>
            <TableCell sx={headCellSx}>Last handshake</TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              colSpan={2}
              align="left"
              sx={{ fontFamily: "Mulish", wordBreak: "break-all" }}
            >
              {subnetsText}
            </TableCell>
            <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
              {snapTunnel.subnets_are_virtually_assigned
                ? checkedCircleIcon
                : cancelIconRed}
            </TableCell>
            <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
              {snapTunnel.tunnel_status ?? "—"}
            </TableCell>
            <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
              {formatWireguardLastHandshake(snapTunnel.last_handshake)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};
